/**
 * The Line's server pushes: state (open/closing/closed with the seconds) and
 * the relayed RouterEvents of a spoken turn (for the receipt card). Only the
 * session this browser holds is honored; another tab's line, or a stale row
 * closing under its old id after a server restart, is ignored.
 */
import type { Socket } from "socket.io-client";
import {
  LINE_CLOSE_ROLLOVER,
  routeFor,
  type LiveEventPayload,
  type LiveStatePayload,
  type RouterEvent,
} from "@bash/shared";
import { EVENTS } from "@/constants/events";
import { isDockWindow, trackDockTask } from "@/lib/dock-window";
import { isDockVisible } from "@/lib/home-flags";
import { applyLinePanel } from "@/components/composer/live/linePanels";
import {
  tellLineMuted,
  useLiveStore,
} from "@/components/composer/live/liveStore";

export function registerLiveHandlers(socket: Socket): () => void {
  // The delegation whose events fill `turn`; a new one empties it first.
  let lastDelegation: string | null = null;

  // Strict: a held id that matches. While we hold none (connecting, or off)
  // a relayed event can only be an older line's turn still running, or
  // another tab's; the server relays every event of a running turn whatever
  // the line's state, so the browser is the one that must not listen.
  const mine = (sessionId: string): boolean =>
    useLiveStore.getState().line.sessionId === sessionId;

  const onState = (p: LiveStatePayload) => {
    // A state push needs a held id that matches. While we hold none
    // (connecting, or off) any push can only be an older line's: the door
    // closes those before it creates ours, and our own line's start and
    // death both also reach the hook through the data channel.
    const held = useLiveStore.getState().line.sessionId;
    if (!held || held !== p.sessionId) return;
    // The deaf call's server half (R-D1): two facts the browser cannot see for
    // itself, on the frame it already listens to. Absent (an older server, or
    // the flag off) leaves them as they are, and false never dials.
    if (p.awaitingVoice !== undefined || p.heard !== undefined)
      useLiveStore.setState({
        awaitingVoice: p.awaitingVoice === true,
        heard: p.heard === true,
      });
    // Attach on the call: the files waiting on the line, a level on every
    // frame of a line that takes them. Absent leaves it as it is, never 0.
    // Every frame that carries it is counted, so its readers act on frames.
    const staged = p.staged;
    if (staged !== undefined)
      useLiveStore.setState((s) => ({
        staged,
        stagedFrames: s.stagedFrames + 1,
      }));
    // The line stays on (T8b): the Mac's line ends at its cap only to go on,
    // so the page dials again once it is off (useLine). Raised BEFORE the
    // state lands: a closed frame that beats the channel takes it off at once.
    // Only on a line that is still OPEN: one he is already hanging up stays
    // hung up, whatever the server's cap tick had on the wire.
    if (
      p.reason === LINE_CLOSE_ROLLOVER &&
      useLiveStore.getState().line.phase === "open"
    )
      useLiveStore.setState({ rollingOver: true });
    useLiveStore.getState().dispatch({
      type: "server_state",
      state: p.state,
      reason: p.reason,
      seconds: p.seconds,
      at: Date.now(),
    });
  };

  const onEvent = (p: LiveEventPayload) => {
    if (!mine(p.sessionId)) return;
    const s = useLiveStore.getState();
    const ev = p.event as RouterEvent;
    // A typed turn relays with no delegation id; its inline card fills from
    // the SSE stream, so only a spoken (delegated) turn lands in the receipt.
    // The receipt renders routing, routed, error and credits, never the
    // words: a text delta would only copy the array and re-render the Dock
    // for every token Bash speaks.
    if (p.delegationId) {
      if (p.delegationId !== lastDelegation) {
        lastDelegation = p.delegationId;
        // A new delegation empties the turn and stamps its id, so the
        // receipt can tell this delegation's late trouble from the next
        // delegation's first (the wire never relays the thread, so a new
        // delegation can open with an error as its literal first event).
        useLiveStore.setState({ turn: [], turnDelegationId: p.delegationId });
      }
      if (ev.type !== "text_delta") s.pushTurnEvent(ev);
      // Composer panels (spec 2026-09-19 §3.4): the crown's one panel, or a door (linePanels.ts).
      if (ev.type === "panel") applyLinePanel(ev.panel);
      // The Line moves you (spec 2026-09-17 §5.4): a navigation or a link the
      // composer answered a SPOKEN ask with is parked for the shell's consumer.
      // In the shell's Dock window "show my tasks" is parked too (the Dock's
      // shelf), and a task the turn started joins the shell's task feed.
      if (
        ev.type === "action" &&
        (ev.action.kind === "app_control" ||
          ev.action.kind === "open_url" ||
          (ev.action.kind === "show_tasks" && isDockWindow()))
      ) {
        useLiveStore.setState({ pendingAction: ev.action });
      }
      if (ev.type === "action" && ev.action.kind === "task_created") {
        trackDockTask({ taskId: ev.action.taskId, title: ev.action.title });
        // On a call, the task the turn started opens its page (his ruling
        // 2026-09-24, voice only; Ankur and Adarsh in #bugs): a typed ask
        // still gets a receipt. Only where the task page keeps the Dock, or
        // the navigation would take the ball off screen and hang up the call.
        const route = routeFor("task", ev.action.taskId);
        if (isDockVisible(route))
          useLiveStore.setState({
            pendingAction: {
              kind: "app_control",
              view: "task",
              route,
              label: "Task",
            },
          });
      }
    }
    s.dispatch({ type: "router", event: ev });
  };

  /**
   * The mute is the one thing about a line the SERVER cannot see for itself, and a flip sent while
   * the socket was down is simply gone: a lost unmute would leave the server believing the mic is
   * off and close the line on the muted bound, mid-conversation. So every (re)connect re-states
   * the level, the way the screen beacon re-states the screen. The server heals on speech too;
   * this is the belt that does not wait for them to talk. A silent mic (2026-09-21) rides the
   * same rail and is lost the same way, so it is re-stated with the mute while it holds; a healthy
   * line's frame stays the mute alone.
   */
  const onConnect = () => {
    const s = useLiveStore.getState();
    if (s.handle) tellLineMuted(s.muted, s.micTrouble ? true : undefined);
  };

  socket.on("connect", onConnect);
  socket.on(EVENTS.COMPOSER_LIVE_STATE, onState);
  socket.on(EVENTS.COMPOSER_LIVE_EVENT, onEvent);
  return () => {
    socket.off("connect", onConnect);
    socket.off(EVENTS.COMPOSER_LIVE_STATE, onState);
    socket.off(EVENTS.COMPOSER_LIVE_EVENT, onEvent);
  };
}
